/**
 * Drug Program Edit Modal
 * 
 * Comprehensive modal for editing drug programs with pharmacy configurations
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { pharmacyAPI, drugProgramAPI, storageAPI, apiHelpers, getProductImageUrl } from '../services/api'
import useDraftStore from '../stores/draftStore'
import { useAuthStore } from '../stores/authStore'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  Button,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Textarea,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator
} from './ui'

const DOSESPOT_TYPES = ['compound', 'brand', 'generic']
const DOSAGE_TYPES = ['oral', 'injectable', 'topical', 'nasal', 'patch']
const DISPENSE_UNITS = ['tablet', 'capsule', 'vial', 'bottle', 'tube', 'patch', 'inhaler', 'syringe']

export default function DrugProgramEditModal({ program, isOpen, onClose, onSave, isDraftEdit = false }) {
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const [formData, setFormData] = useState(program || {})
  const [validationErrors, setValidationErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [editingPharmacy, setEditingPharmacy] = useState(null)
  const [availablePharmacies, setAvailablePharmacies] = useState([])
  const [loadingPharmacies, setLoadingPharmacies] = useState(false)
  const [availablePrograms, setAvailablePrograms] = useState([])
  const [loadingPrograms, setLoadingPrograms] = useState(false)
  const [productImages, setProductImages] = useState([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [showImagePicker, setShowImagePicker] = useState(false)

  const { currentClinicId } = useAuthStore()
  const clinicId = currentClinicId || searchParams.get('clinic') || 'tmates'

  // Initialize draft store when modal opens
  useEffect(() => {
    if (isOpen && user && clinicId) {
      const { initialize, isInitialized, userId: storeUserId } = useDraftStore.getState()
      
      // Get user ID (backend uses 'id', not '_id')
      const userId = user.id
      
      // Re-initialize if store is not initialized OR if it was initialized with wrong userId
      if (!isInitialized || storeUserId !== userId) {
        initialize(clinicId, userId)
      }
    }
  }, [isOpen, user, clinicId])

  useEffect(() => {
    if (program) {
      setFormData({
        drug_name: program.drug_name || '',
        entity_id: program.entity_id || '',
        dosespot_type: program.dosespot_type || 'compound',
        dea: program.dea ?? false,
        local_pharmacy: program.local_pharmacy ?? false,
        sync_required: program.sync_required ?? false,
        can_create_order: program.can_create_order ?? true,
        marketing_name: program.marketing_name || '',
        tags: program.tags || [],
        dosage: {
          dosage: program.dosage?.dosage || '',
          type: program.dosage?.type || 'oral',
          period: program.dosage?.period || '',
          weeks_supply: program.dosage?.weeks_supply || 4,
          description: program.dosage?.description || '',
          img: program.dosage?.img || ''
        },
        fill_schedule: program.fill_schedule || null,
        pharmacies: program.pharmacies || []
      })
      setHasChanges(false)
    }
  }, [program])

  // Fetch available pharmacies, programs, and product images when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailablePharmacies()
      fetchAvailablePrograms()
      fetchProductImages()
    }
  }, [isOpen])

  const fetchAvailablePharmacies = async () => {
    setLoadingPharmacies(true)
    try {
      const response = await pharmacyAPI.list({ 
        limit: 1000, // Get all pharmacies
        include_disabled: false // Only active pharmacies
      })
      if (response.data.success) {
        setAvailablePharmacies(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch pharmacies:', error)
      // Don't show error to user, just log it
      setAvailablePharmacies([])
    } finally {
      setLoadingPharmacies(false)
    }
  }

  const fetchAvailablePrograms = async () => {
    setLoadingPrograms(true)
    try {
      const response = await drugProgramAPI.list({
        limit: 1000 // Get all programs
      })
      if (response.data.success) {
        // Filter out programs with fill_schedule to avoid nesting
        const programs = (response.data.data || []).filter(p => !p.fill_schedule)
        setAvailablePrograms(programs)
      }
    } catch (error) {
      console.error('Failed to fetch programs:', error)
      setAvailablePrograms([])
    } finally {
      setLoadingPrograms(false)
    }
  }

  const fetchProductImages = async () => {
    setLoadingImages(true)
    try {
      const response = await storageAPI.browse('products/')
      if (response.data.success) {
        // Filter for image files only
        const imageExtensions = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg']
        const images = (response.data.data.files || []).filter(file => {
          const ext = file.key.split('.').pop().toLowerCase()
          return imageExtensions.includes(ext)
        })
        setProductImages(images)
      }
    } catch (error) {
      console.error('Failed to fetch product images:', error)
      setProductImages([])
    } finally {
      setLoadingImages(false)
    }
  }

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev }
      const keys = path.split('.')
      let current = newData
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {}
        current = current[keys[i]]
      }
      
      current[keys[keys.length - 1]] = value
      return newData
    })
    setHasChanges(true)
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      updateFormData('tags', [...formData.tags, newTag.trim()])
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove) => {
    updateFormData('tags', (formData.tags || []).filter(tag => tag !== tagToRemove))
  }

  const addPharmacy = (selectedPharmacy) => {
    if (!selectedPharmacy) return
    
    // Check if pharmacy is already added
    const currentPharmacies = formData.pharmacies || []
    const alreadyAdded = currentPharmacies.some(p => 
      p.pharmacy_ncpdpid === selectedPharmacy.pharmacy_ncpdpid
    )
    
    if (alreadyAdded) {
      alert('This pharmacy is already added to the program.')
      return
    }
    
    const newPharmacy = {
      pharmacy_name: selectedPharmacy.pharmacy_name,
      pharmacy_ncpdpid: selectedPharmacy.pharmacy_ncpdpid,
      current_price: 0,
      drugs: [{
        drug_name: formData.drug_name,
        pharmacy_drug_name: formData.drug_name.toUpperCase(),
        current_price: 0,
        days: 30,
        dispense_quantity: 1,
        dispense_unit: 'tablet',
        sig: 'Take as directed',
        click_to_write: 'True'
      }]
    }
    updateFormData('pharmacies', [...currentPharmacies, newPharmacy])
  }

  // Get pharmacies that haven't been added yet
  const getAvailablePharmaciesForSelection = () => {
    const addedNCPDPIds = new Set((formData.pharmacies || []).map(p => p.pharmacy_ncpdpid))
    return availablePharmacies.filter(pharmacy => 
      !addedNCPDPIds.has(pharmacy.pharmacy_ncpdpid)
    )
  }

  const updatePharmacy = (index, field, value) => {
    const updatedPharmacies = [...(formData.pharmacies || [])]
    if (field.startsWith('drugs.0.')) {
      const drugField = field.replace('drugs.0.', '')
      updatedPharmacies[index].drugs[0][drugField] = value
    } else {
      updatedPharmacies[index][field] = value
    }
    updateFormData('pharmacies', updatedPharmacies)
  }

  const removePharmacy = (index) => {
    const updatedPharmacies = (formData.pharmacies || []).filter((_, i) => i !== index)
    updateFormData('pharmacies', updatedPharmacies)
    if (editingPharmacy === index) setEditingPharmacy(null)
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.drug_name?.trim()) {
      errors.drug_name = 'Drug name is required'
    }
    
    if (!formData.entity_id?.trim()) {
      errors.entity_id = 'Entity ID is required'
    }
    
    if (!formData.dosespot_type) {
      errors.dosespot_type = 'DoseSpot type is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return
    
    setIsLoading(true)
    try {
      if (isDraftEdit) {
        onSave?.(formData)
        onClose()
        setHasChanges(false)
      } else {
        const { addChange } = useDraftStore.getState()

        const isNewProgram = !program || !program._id
        const operation = isNewProgram ? 'create' : 'update'

        const details = []
        if (formData.entity_id) details.push(`ID: ${formData.entity_id}`)
        if (formData.dosespot_type) details.push(`Type: ${formData.dosespot_type}`)
        if (formData.pharmacies?.length) details.push(`${formData.pharmacies.length} pharmacies`)
        if (formData.dea) details.push('DEA required')

        const description = operation === 'create'
          ? `Create: ${formData.drug_name} (${details.join(', ')})`
          : `Update: ${formData.drug_name} (${details.join(', ')})`

        addChange({
          resource_type: 'drug_program',
          resource_id: program?._id || program?.entity_id || formData.entity_id,
          operation: operation,
          original_data: program || null,
          new_data: formData,
          description: description
        })

        console.log(`[DRAFT] Saved ${operation} for drug program:`, formData.drug_name)

        onSave?.(formData)
        onClose()
        setHasChanges(false)
      }
    } catch (error) {
      console.error('Failed to save drug program to drafts:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose()
        setHasChanges(false)
      }
    } else {
      onClose()
    }
  }

  const formatPrice = (price) => {
    if (!price) return '$0.00'
    return `$${(price / 100).toFixed(2)}`
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {program ? `Edit Program - ${program.drug_name}` : 'Create New Drug Program'}
          </DialogTitle>
          <DialogDescription>
            Update drug program information, dosage details, and pharmacy configurations.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="dosage">Dosage & Schedule</TabsTrigger>
            <TabsTrigger value="pharmacies">Pharmacy Config</TabsTrigger>
            <TabsTrigger value="tags">Tags & Meta</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="drug_name">Drug Name *</Label>
                <Input
                  id="drug_name"
                  value={formData.drug_name || ''}
                  onChange={(e) => updateFormData('drug_name', e.target.value)}
                  className={validationErrors.drug_name ? 'border-red-500' : ''}
                />
                {validationErrors.drug_name && (
                  <p className="text-sm text-red-500">{validationErrors.drug_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="entity_id">Entity ID *</Label>
                <Input
                  id="entity_id"
                  value={formData.entity_id || ''}
                  onChange={(e) => updateFormData('entity_id', e.target.value)}
                  className={validationErrors.entity_id ? 'border-red-500' : ''}
                />
                {validationErrors.entity_id && (
                  <p className="text-sm text-red-500">{validationErrors.entity_id}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dosespot_type">DoseSpot Type *</Label>
                <Select
                  value={formData.dosespot_type || 'compound'}
                  onValueChange={(value) => updateFormData('dosespot_type', value)}
                >
                  <SelectTrigger className={validationErrors.dosespot_type ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOSESPOT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.dosespot_type && (
                  <p className="text-sm text-red-500">{validationErrors.dosespot_type}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="marketing_name">Marketing Name</Label>
                <Input
                  id="marketing_name"
                  value={formData.marketing_name || ''}
                  onChange={(e) => updateFormData('marketing_name', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dea"
                    checked={formData.dea ?? false}
                    onCheckedChange={(checked) => updateFormData('dea', checked)}
                  />
                  <Label htmlFor="dea">DEA Required</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="local_pharmacy"
                    checked={formData.local_pharmacy ?? false}
                    onCheckedChange={(checked) => updateFormData('local_pharmacy', checked)}
                  />
                  <Label htmlFor="local_pharmacy">Local Pharmacy Only</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sync_required"
                    checked={formData.sync_required ?? false}
                    onCheckedChange={(checked) => updateFormData('sync_required', checked)}
                  />
                  <Label htmlFor="sync_required">Sync Required</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="can_create_order"
                    checked={formData.can_create_order ?? true}
                    onCheckedChange={(checked) => updateFormData('can_create_order', checked)}
                  />
                  <Label htmlFor="can_create_order">Can Create Order</Label>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Dosage & Schedule Tab */}
          <TabsContent value="dosage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Dosage Information</CardTitle>
                <CardDescription>Configure dosing schedule and supply details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dosage_amount">Dosage</Label>
                    <Input
                      id="dosage_amount"
                      value={formData.dosage?.dosage || ''}
                      onChange={(e) => updateFormData('dosage.dosage', e.target.value)}
                      placeholder="e.g., 1mg, 500mg, 10ml"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dosage_type">Dosage Type</Label>
                    <Select
                      value={formData.dosage?.type || 'oral'}
                      onValueChange={(value) => updateFormData('dosage.type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {DOSAGE_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="period">Period</Label>
                    <input
                      id="period"
                      type="text"
                      value={formData.dosage?.period || ''}
                      onChange={(e) => updateFormData('dosage.period', e.target.value)}
                      placeholder="e.g. daily, weekly, 30 days"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="weeks_supply">Weeks Supply</Label>
                    <Input
                      id="weeks_supply"
                      type="number"
                      min="1"
                      max="52"
                      value={formData.dosage?.weeks_supply || 4}
                      onChange={(e) => updateFormData('dosage.weeks_supply', parseInt(e.target.value) || 4)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dosage_description">Description</Label>
                  <Textarea
                    id="dosage_description"
                    value={formData.dosage?.description || ''}
                    onChange={(e) => updateFormData('dosage.description', e.target.value)}
                    placeholder="Detailed dosing instructions or notes..."
                    rows={3}
                  />
                </div>

                <Separator />

                {/* Product Image Selection */}
                <div className="space-y-3">
                  <Label>Product Image</Label>
                  <div className="flex items-start gap-4">
                    {/* Current Image Preview */}
                    <div className="flex-shrink-0">
                      {formData.dosage?.img ? (
                        <div className="relative">
                          <img
                            src={getProductImageUrl(formData.dosage.img)}
                            alt="Product"
                            className="w-24 h-24 rounded-lg object-contain bg-muted border"
                            onError={(e) => {
                              e.target.src = ''
                              e.target.className = 'w-24 h-24 rounded-lg bg-muted border flex items-center justify-center'
                              e.target.alt = 'Image not found'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => updateFormData('dosage.img', '')}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-destructive/80"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-muted border border-dashed flex items-center justify-center text-muted-foreground text-xs text-center p-2">
                          No image selected
                        </div>
                      )}
                    </div>

                    {/* Image Selection */}
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={formData.dosage?.img || ''}
                          onChange={(e) => updateFormData('dosage.img', e.target.value)}
                          placeholder="/assets/img/products/filename.png"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowImagePicker(!showImagePicker)}
                        >
                          {showImagePicker ? 'Hide' : 'Browse'}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Path relative to S3 bucket (e.g., /assets/img/products/drug.png)
                      </p>

                      {/* Image Picker Grid */}
                      {showImagePicker && (
                        <div className="border rounded-lg p-3 bg-muted/30 max-h-48 overflow-y-auto">
                          {loadingImages ? (
                            <div className="text-center text-muted-foreground py-4">Loading images...</div>
                          ) : productImages.length === 0 ? (
                            <div className="text-center text-muted-foreground py-4">
                              No images found in products/ folder
                            </div>
                          ) : (
                            <div className="grid grid-cols-6 gap-2">
                              {productImages.map((image) => {
                                const filename = image.key.split('/').pop()
                                const imgPath = `/assets/img/products/${filename}`
                                const isSelected = formData.dosage?.img === imgPath
                                return (
                                  <button
                                    key={image.key}
                                    type="button"
                                    onClick={() => {
                                      updateFormData('dosage.img', imgPath)
                                      setShowImagePicker(false)
                                    }}
                                    className={`relative group p-1 rounded border-2 transition-all ${
                                      isSelected
                                        ? 'border-primary bg-primary/10'
                                        : 'border-transparent hover:border-primary/50'
                                    }`}
                                    title={filename}
                                  >
                                    <img
                                      src={`https://5axis-intake-public.s3.amazonaws.com/${image.key}`}
                                      alt={filename}
                                      className="w-12 h-12 object-contain rounded"
                                      onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                                      <span className="text-white text-xs truncate px-1">{filename}</span>
                                    </div>
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fill Schedule (Phased/Titration Programs) */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Fill Schedule (Phased Program)</CardTitle>
                    <CardDescription>
                      Configure titration/phasing schedule for multi-month programs
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {formData.fill_schedule && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (confirm('Remove fill schedule? This will make it a standard single-dose program.')) {
                            updateFormData('fill_schedule', null)
                          }
                        }}
                      >
                        Remove Schedule
                      </Button>
                    )}
                    {!formData.fill_schedule && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          updateFormData('fill_schedule', {
                            total_fills: 3,
                            fill: [
                              { days_since_last_fill: 0, drug: '', program_entity: '' },
                              { days_since_last_fill: 30, drug: '', program_entity: '' },
                              { days_since_last_fill: 30, drug: '', program_entity: '' }
                            ]
                          })
                        }}
                      >
                        Enable Fill Schedule
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {formData.fill_schedule && (
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="total_fills">Total Fills</Label>
                    <Input
                      id="total_fills"
                      type="number"
                      min="1"
                      max="12"
                      value={formData.fill_schedule?.total_fills || 3}
                      onChange={(e) => updateFormData('fill_schedule.total_fills', parseInt(e.target.value) || 3)}
                    />
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Fill Phases ({formData.fill_schedule?.fill?.length || 0} phases)</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const currentFills = formData.fill_schedule?.fill || []
                          const newFills = [
                            ...currentFills,
                            {
                              days_since_last_fill: 30,
                              drug: '', // Will be set when program is selected from dropdown
                              program_entity: '' // Will be set when program is selected from dropdown
                            }
                          ]
                          updateFormData('fill_schedule.fill', newFills)
                        }}
                      >
                        Add Phase
                      </Button>
                    </div>

                    {formData.fill_schedule?.fill?.map((fill, index) => (
                      <Card key={index} className="border-l-4 border-l-primary">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">
                              Phase {index + 1} {index === 0 && '(Initial Fill)'}
                            </CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newFills = formData.fill_schedule.fill.filter((_, i) => i !== index)
                                updateFormData('fill_schedule.fill', newFills)
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              Remove
                            </Button>
                          </div>
                          <CardDescription>
                            {index === 0 ? 'Day 0' : `${formData.fill_schedule.fill.slice(0, index + 1).reduce((sum, f) => sum + f.days_since_last_fill, 0)} days from start`}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2">
                              <Label>Days Since Last Fill</Label>
                              <Input
                                type="number"
                                min="0"
                                value={fill.days_since_last_fill}
                                onChange={(e) => {
                                  const newFills = [...formData.fill_schedule.fill]
                                  newFills[index].days_since_last_fill = parseInt(e.target.value) || 0
                                  updateFormData('fill_schedule.fill', newFills)
                                }}
                                disabled={index === 0}
                                className={index === 0 ? 'bg-muted' : ''}
                              />
                            </div>
                            <div className="col-span-2 space-y-2">
                              <Label>Select Program (or edit manually below)</Label>
                              <Select
                                value={fill.program_entity || undefined}
                                onValueChange={(value) => {
                                  const selectedProgram = availablePrograms.find(p => p.entity_id === value)
                                  if (selectedProgram) {
                                    const newFills = [...formData.fill_schedule.fill]
                                    newFills[index].drug = selectedProgram.drug_name
                                    newFills[index].program_entity = selectedProgram.entity_id
                                    updateFormData('fill_schedule.fill', newFills)
                                  }
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={
                                    loadingPrograms 
                                      ? "Loading programs..." 
                                      : availablePrograms.length === 0
                                        ? "No programs available"
                                        : "Select a program"
                                  } />
                                </SelectTrigger>
                                <SelectContent>
                                  {loadingPrograms ? (
                                    <SelectItem value="loading" disabled>Loading programs...</SelectItem>
                                  ) : availablePrograms.length === 0 ? (
                                    <SelectItem value="none" disabled>No single-fill programs available</SelectItem>
                                  ) : (
                                    availablePrograms.map((prog) => (
                                      <SelectItem key={prog._id} value={prog.entity_id}>
                                        <div className="flex flex-col">
                                          <span className="font-medium">{prog.drug_name}</span>
                                          <span className="text-xs text-muted-foreground">
                                            {prog.entity_id} • {prog.dosespot_type}
                                            {prog.dosage?.dosage && ` • ${prog.dosage.dosage}`}
                                          </span>
                                        </div>
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label>Drug Name</Label>
                              <Input
                                value={fill.drug}
                                onChange={(e) => {
                                  const newFills = [...formData.fill_schedule.fill]
                                  newFills[index].drug = e.target.value
                                  updateFormData('fill_schedule.fill', newFills)
                                }}
                                placeholder="e.g. Semaglutide"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Program Entity ID</Label>
                              <Input
                                value={fill.program_entity}
                                onChange={(e) => {
                                  const newFills = [...formData.fill_schedule.fill]
                                  newFills[index].program_entity = e.target.value
                                  updateFormData('fill_schedule.fill', newFills)
                                }}
                                placeholder="e.g. Semaglutide-Level-1-USD-Monthly"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {(!formData.fill_schedule?.fill || formData.fill_schedule.fill.length === 0) && (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No fill phases configured.</p>
                        <p className="text-sm">Click "Add Phase" to create a phasing schedule.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}

              {!formData.fill_schedule && (
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="mb-2">📅 Fill schedule not configured</p>
                    <p className="text-sm">This is a standard single-dose program.</p>
                    <p className="text-sm">Enable fill schedule for titration/phasing programs.</p>
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Pharmacy Configuration Tab */}
          <TabsContent value="pharmacies" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Pharmacy Configurations</h3>
                <p className="text-sm text-muted-foreground">
                  Configure this program for specific pharmacies ({formData.pharmacies?.length || 0} configured)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select onValueChange={(value) => {
                  const selectedPharmacy = availablePharmacies.find(p => p._id === value)
                  if (selectedPharmacy) addPharmacy(selectedPharmacy)
                }}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder={
                      loadingPharmacies 
                        ? "Loading pharmacies..." 
                        : getAvailablePharmaciesForSelection().length === 0 
                          ? "All pharmacies added"
                          : "Add pharmacy"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingPharmacies ? (
                      <SelectItem value="loading" disabled>Loading pharmacies...</SelectItem>
                    ) : getAvailablePharmaciesForSelection().length === 0 ? (
                      <SelectItem value="none" disabled>All pharmacies have been added</SelectItem>
                    ) : (
                      getAvailablePharmaciesForSelection().map((pharmacy) => (
                        <SelectItem key={pharmacy._id} value={pharmacy._id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{pharmacy.pharmacy_name}</span>
                            <span className="text-xs text-muted-foreground">
                              NCPDP: {pharmacy.pharmacy_ncpdpid} • {pharmacy.pharmacy_gateway} • {pharmacy.states?.length || 0} states
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              {formData.pharmacies?.map((pharmacy, index) => (
                <Card key={index} className={`${editingPharmacy === index ? 'border-primary' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {pharmacy.pharmacy_name || `Pharmacy ${index + 1}`}
                        </CardTitle>
                        <CardDescription>
                          NCPDP: {pharmacy.pharmacy_ncpdpid} • <span className="font-semibold text-primary">Ingredient: {formatPrice(pharmacy.drugs?.[0]?.current_price)}</span>
                          {(pharmacy.current_price || 0) > 0 && ` + ${formatPrice(pharmacy.current_price)} shipping`}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingPharmacy(editingPharmacy === index ? null : index)}
                        >
                          {editingPharmacy === index ? 'Collapse' : 'Edit'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removePharmacy(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  {editingPharmacy === index && (
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Pharmacy Name</Label>
                          <Input
                            value={pharmacy.pharmacy_name}
                            disabled
                            className="bg-muted"
                            title="Pharmacy name cannot be changed (selected from dropdown)"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>NCPDP ID</Label>
                          <Input
                            value={pharmacy.pharmacy_ncpdpid}
                            disabled
                            className="bg-muted"
                            title="NCPDP ID cannot be changed (selected from dropdown)"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Shipping/Handling (cents) - optional</Label>
                          <Input
                            value={pharmacy.current_price || 0}
                            onChange={(e) => updatePharmacy(index, 'current_price', parseInt(e.target.value) || 0)}
                            placeholder="Shipping/handling in cents (optional)"
                            type="number"
                            min="0"
                            className="text-muted-foreground"
                          />
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <h4 className="font-medium mb-3">Drug Configuration</h4>
                        
                        {/* Ingredient Price - Most Important Field */}
                        <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                          <div className="space-y-2">
                            <Label className="text-primary font-semibold">💰 Ingredient Price (cents) *</Label>
                            <Input
                              type="number"
                              min="0"
                              value={pharmacy.drugs[0]?.current_price || 0}
                              onChange={(e) => updatePharmacy(index, 'drugs.0.current_price', parseInt(e.target.value) || 0)}
                              placeholder="Drug ingredient price in cents"
                              className="border-primary/30 focus:border-primary text-lg font-semibold"
                            />
                            <p className="text-xs text-muted-foreground">This is the main price used for routing and display</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Pharmacy Drug Name</Label>
                            <Input
                              value={pharmacy.drugs[0]?.pharmacy_drug_name || ''}
                              onChange={(e) => updatePharmacy(index, 'drugs.0.pharmacy_drug_name', e.target.value)}
                              placeholder="How pharmacy names this drug"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Days Supply</Label>
                            <Input
                              type="number"
                              min="1"
                              value={pharmacy.drugs[0]?.days || 30}
                              onChange={(e) => updatePharmacy(index, 'drugs.0.days', parseInt(e.target.value) || 30)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Dispense Quantity</Label>
                            <Input
                              type="number"
                              min="1"
                              value={pharmacy.drugs[0]?.dispense_quantity || 1}
                              onChange={(e) => updatePharmacy(index, 'drugs.0.dispense_quantity', parseInt(e.target.value) || 1)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Dispense Unit</Label>
                            <Select
                              value={pharmacy.drugs[0]?.dispense_unit || 'tablet'}
                              onValueChange={(value) => updatePharmacy(index, 'drugs.0.dispense_unit', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DISPENSE_UNITS.map((unit) => (
                                  <SelectItem key={unit} value={unit}>
                                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2 mt-4">
                          <Label>Sig (Instructions)</Label>
                          <Textarea
                            value={pharmacy.drugs[0]?.sig || ''}
                            onChange={(e) => updatePharmacy(index, 'drugs.0.sig', e.target.value)}
                            placeholder="e.g., Take 1 tablet daily with food"
                            rows={2}
                          />
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}

              {formData.pharmacies?.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No pharmacy configurations added yet.</p>
                  <p className="text-sm">Select a pharmacy from the dropdown above to configure this program for specific pharmacies.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tags & Meta Tab */}
          <TabsContent value="tags" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label>Program Tags</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag..."
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    Add
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.tags?.map((tag, index) => (
                  <Badge key={index} variant="outline" className="cursor-pointer hover:bg-red-50">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>

              {formData.tags?.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No tags added. Tags help categorize and filter programs.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || !hasChanges}>
            {isLoading ? 'Saving to Drafts...' : 'Save to Drafts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
